import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from './ThemeProvider';
import { motion } from 'motion/react';
import { X, ZoomIn, ZoomOut, Move, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';

console.log('[ImageCropperModal] Module loaded');

interface ImageCropperModalProps {
    isOpen: boolean;
    onClose: () => void;
    imageUrl: string;
    onSave: (croppedImageUrl: string) => void;
    aspectRatio?: number; // width / height
    title?: string;
    description?: string;
    circular?: boolean;
    outputWidth?: number; // Cropped output width in px (height derived from aspectRatio). Default 600.
    onUploadNew?: () => void; // Optional: triggers a new file pick from parent
}

export function ImageCropperModal({
    isOpen,
    onClose,
    imageUrl,
    onSave,
    aspectRatio = 1,
    title = "Adjust Image",
    description = "Drag to reposition • Zoom to fit",
    circular = false,
    outputWidth = 600,
    onUploadNew
}: ImageCropperModalProps) {
    const { theme } = useTheme();
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0, naturalWidth: 0, naturalHeight: 0 });
    const [minScale, setMinScale] = useState(1);
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    // Load image dimensions when modal opens
    useEffect(() => {
        if (isOpen && imageUrl) {
            const img = new Image();
            img.onload = () => {
                if (containerRef.current) {
                    const containerWidth = containerRef.current.offsetWidth;
                    const containerHeight = containerRef.current.offsetHeight;
                    const imgRatio = img.naturalWidth / img.naturalHeight;
                    const containerRatio = containerWidth / containerHeight;

                    let renderWidth, renderHeight;
                    if (imgRatio > containerRatio) {
                        renderHeight = containerHeight;
                        renderWidth = containerHeight * imgRatio;
                    } else {
                        renderWidth = containerWidth;
                        renderHeight = containerWidth / imgRatio;
                    }

                    setImageDimensions({
                        width: renderWidth,
                        height: renderHeight,
                        naturalWidth: img.naturalWidth,
                        naturalHeight: img.naturalHeight
                    });
                    
                    setMinScale(1);
                    setScale(1);
                }
            };
            img.src = imageUrl;
            setPosition({ x: 0, y: 0 });
        }
    }, [isOpen, imageUrl, aspectRatio]);

    // Clamp position when scale changes
    useEffect(() => {
        if (containerRef.current && imageDimensions.width > 0) {
            const containerWidth = containerRef.current.offsetWidth;
            const containerHeight = containerRef.current.offsetHeight;
            
            const scaledWidth = imageDimensions.width * scale;
            const scaledHeight = imageDimensions.height * scale;
            
            const maxX = Math.max(0, (scaledWidth - containerWidth) / 2);
            const maxY = Math.max(0, (scaledHeight - containerHeight) / 2);

            setPosition(prev => ({
                x: Math.max(-maxX, Math.min(prev.x, maxX)),
                y: Math.max(-maxY, Math.min(prev.y, maxY))
            }));
        }
    }, [scale, imageDimensions]);

    // Drag Logic (Universal Touch + Mouse)
    const handleDragStart = (clientX: number, clientY: number) => {
        setIsDragging(true);
        setDragStart({ x: clientX - position.x, y: clientY - position.y });
    };

    const handleDragMove = (clientX: number, clientY: number) => {
        if (!isDragging || !containerRef.current || imageDimensions.width === 0) return;
        
        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = containerRef.current.offsetHeight;
        
        const scaledWidth = imageDimensions.width * scale;
        const scaledHeight = imageDimensions.height * scale;
        
        const maxX = Math.max(0, (scaledWidth - containerWidth) / 2);
        const maxY = Math.max(0, (scaledHeight - containerHeight) / 2);

        const newX = clientX - dragStart.x;
        const newY = clientY - dragStart.y;

        setPosition({
            x: Math.max(-maxX, Math.min(newX, maxX)),
            y: Math.max(-maxY, Math.min(newY, maxY))
        });
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        handleDragStart(e.clientX, e.clientY);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        handleDragMove(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length === 1) {
            handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (e.touches.length === 1) {
            handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
        }
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    const handleSave = async () => {
        if (!containerRef.current) return;

        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            const outputHeight = outputWidth / aspectRatio;

            canvas.width = outputWidth;
            canvas.height = outputHeight;

            const img = new Image();
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = imageUrl;
            });

            const containerWidth = containerRef.current.offsetWidth;
            const containerHeight = containerRef.current.offsetHeight;

            const finalRenderWidth = imageDimensions.width * scale;
            const finalRenderHeight = imageDimensions.height * scale;

            const initialX = (containerWidth - finalRenderWidth) / 2;
            const initialY = (containerHeight - finalRenderHeight) / 2;

            const drawX = initialX + position.x;
            const drawY = initialY + position.y;

            const scaleFactor = outputWidth / containerWidth;

            ctx.fillStyle = '#000000';
            ctx.fillRect(0, 0, outputWidth, outputHeight);

            ctx.save();
            if (circular) {
                ctx.beginPath();
                ctx.arc(outputWidth / 2, outputHeight / 2, outputWidth / 2, 0, Math.PI * 2);
                ctx.closePath();
                ctx.clip();
            }

            ctx.drawImage(
                img,
                drawX * scaleFactor,
                drawY * scaleFactor,
                finalRenderWidth * scaleFactor,
                finalRenderHeight * scaleFactor
            );

            ctx.restore();

            const croppedImageUrl = canvas.toDataURL('image/jpeg', 0.95);
            onSave(croppedImageUrl);
        } catch (error) {
            console.error('Error cropping image:', error);
            onSave(imageUrl);
        }
    };

    const handleReset = () => {
        setScale(minScale);
        setPosition({ x: 0, y: 0 });
    };

    if (!isOpen) return null;

    const modalContent = (
        <div
            className="fixed inset-0 bg-black/95 flex items-center justify-center p-4 backdrop-blur-sm"
            style={{ zIndex: 10000, pointerEvents: 'auto' }}
        >
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="w-full max-w-lg bg-[#ffffff] dark:bg-[#1a1a1a] rounded-3xl overflow-hidden shadow-2xl"
                style={{ zIndex: 10001, pointerEvents: 'auto' }}
            >
                {/* Header */}
                <div className="p-4 border-b border-black/10 dark:border-white/10 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-black dark:text-white">{title}</h2>
                    <button
                        onClick={onClose}
                        className="text-[#666666] dark:text-[#E5E5E5] hover:text-black dark:hover:text-white p-2"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                        {/* Image Preview Container */}
                        <div className="p-6">
                            <div className="mb-4">
                                <p className="text-sm text-[#373737] dark:text-[#E5E5E5] text-center mb-2">
                                    {description}
                                </p>
                            </div>

                            {/* Crop Area */}
                            <div className="flex justify-center mb-6">
                                <div
                                    ref={containerRef}
                                    className={`relative bg-black/20 dark:bg-white/5 overflow-hidden border-4 cursor-move ${circular ? 'rounded-full' : 'rounded-none'}`}
                                    style={{
                                        width: '100%',
                                        maxWidth: '400px',
                                        aspectRatio: `${aspectRatio}`,
                                        borderColor: theme === 'dark' ? '#3B82F6' : '#F39C12'
                                    }}
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                    onMouseLeave={handleMouseUp}
                                    onTouchStart={handleTouchStart}
                                    onTouchMove={handleTouchMove}
                                    onTouchEnd={handleTouchEnd}
                                    onTouchCancel={handleTouchEnd}
                                >
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <motion.img
                                            src={imageUrl}
                                            alt="Crop preview"
                                            className="absolute max-w-none select-none"
                                            style={{
                                                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                                                width: imageDimensions.width > 0 ? `${imageDimensions.width}px` : '100%',
                                                height: imageDimensions.height > 0 ? `${imageDimensions.height}px` : '100%',
                                                pointerEvents: 'none'
                                            }}
                                            draggable={false}
                                        />
                                    </div>

                                    {/* Grid Overlay */}
                                    <div className="absolute inset-0 pointer-events-none">
                                        <div className="absolute inset-0 border border-white/20"
                                            style={{
                                                width: '33.33%',
                                                height: '100%',
                                                left: '33.33%'
                                            }}
                                        />
                                        <div className="absolute inset-0 border border-white/20"
                                            style={{
                                                width: '100%',
                                                height: '33.33%',
                                                top: '33.33%'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Zoom Slider */}
                            <div className="mb-6">
                                <div className="flex items-center gap-3 mb-2">
                                    <ZoomOut className="w-5 h-5 text-[#666666] dark:text-[#E5E5E5]" />
                                    <Slider
                                        value={[scale]}
                                        onValueChange={(values: number[]) => setScale(Math.max(values[0], minScale))}
                                        min={minScale}
                                        max={3}
                                        step={0.1}
                                        className="flex-1"
                                    />
                                    <ZoomIn className="w-5 h-5 text-[#666666] dark:text-[#E5E5E5]" />
                                </div>
                                <p className="text-xs text-center text-[#999999]">
                                    Zoom: {Math.round(scale * 100)}%
                                </p>
                            </div>

                            {/* Action Buttons */}
                            <div className="relative z-10 flex gap-3">
                                <Button
                                    onClick={handleReset}
                                    variant="outline"
                                    className="flex-1 h-12 border-2 border-black/10 dark:border-white/10 text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/5 rounded-xl"
                                >
                                    Reset
                                </Button>
                                {onUploadNew && (
                                    <Button
                                        onClick={() => { onClose(); setTimeout(onUploadNew, 50); }}
                                        variant="outline"
                                        className="flex-1 h-12 border-2 border-black/10 dark:border-white/10 text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/5 rounded-xl"
                                    >
                                        Upload New
                                    </Button>
                                )}
                                <Button
                                    onClick={handleSave}
                                    className="bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 text-white disabled:opacity-50 min-w-[100px] shadow flex items-center justify-center gap-2"
                                >
                                    <Check className="w-4 h-4" />
                                    Confirm Crop
                                </Button>
                            </div>
                        </div>
                </motion.div>
            </div>
    );

    return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null;
}
